/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, Loader2, Truck, PackageCheck,
  CheckCircle2, Clock, Beaker, Ban, ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const STAGE_ORDER = [
  'payment_confirmed', 'raw_material_ordered', 'raw_material_received',
  'fermentation_started', 'fermentation_completed', 'packing',
  'dispatched', 'delivered', 'execution_scheduled', 'execution_started',
];

const STAGE_LABELS = {
  payment_confirmed: 'Payment',
  raw_material_ordered: 'Raw Ordered',
  raw_material_received: 'Raw Received',
  fermentation_started: 'Ferm. Start',
  fermentation_completed: 'Ferm. Done',
  packing: 'Packing',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  execution_scheduled: 'Exec. Scheduled',
  execution_started: 'Exec. Started',
};

export default function FormulationProduction() {
  const { subscriberId, phaseId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canReview = user?.role === 'admin' || user?.role === 'counselor';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [nextStatus, setNextStatus] = useState('');
  const [moving, setMoving] = useState(false);

  const [delayNote, setDelayNote] = useState('');
  const [delaying, setDelaying] = useState(false);

  const [deliveryDate, setDeliveryDate] = useState('');
  const [courierName, setCourierName] = useState('');
  const [courierTracking, setCourierTracking] = useState('');
  const [delivering, setDelivering] = useState(false);

  const [opsNote, setOpsNote] = useState('');
  const [notesPosting, setNotesPosting] = useState(false);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(`${API}/formulation/production/${subscriberId}/${phaseId}`);
      setData(r.data);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load production record');
    } finally {
      setLoading(false);
    }
  }, [subscriberId, phaseId]);
  useEffect(() => { load(); }, [load]);

  const moveStatus = async () => {
    if (!nextStatus) return;
    setMoving(true);
    try {
      await axios.patch(
        `${API}/formulation/production/${subscriberId}/${phaseId}/status`,
        { to_status: nextStatus, note: delayNote || undefined },
      );
      toast.success(`Status moved to ${nextStatus}`);
      setNextStatus('');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.detail || 'Move failed'));
    } finally {
      setMoving(false);
    }
  };

  const markDelayed = async () => {
    if (!delayNote.trim()) {
      toast.error('Add a delay note before marking delayed');
      return;
    }
    setDelaying(true);
    try {
      await axios.patch(
        `${API}/formulation/production/${subscriberId}/${phaseId}/status`,
        { to_status: 'delayed', note: delayNote },
      );
      toast.success('Marked delayed');
      setDelayNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delay failed');
    } finally {
      setDelaying(false);
    }
  };

  const markDelivered = async () => {
    setDelivering(true);
    try {
      await axios.patch(
        `${API}/formulation/production/${subscriberId}/${phaseId}/status`,
        {
          to_status: 'delivered',
          actual_delivery_date: deliveryDate || undefined,
          courier_name: courierName || undefined,
          courier_tracking: courierTracking || undefined,
        },
      );
      toast.success('Delivered — execution scheduled for next day');
      setDeliveryDate(''); setCourierName(''); setCourierTracking('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delivery mark failed');
    } finally {
      setDelivering(false);
    }
  };

  const addNote = async () => {
    if (!opsNote.trim()) return;
    setNotesPosting(true);
    try {
      await axios.post(
        `${API}/formulation/production/${subscriberId}/${phaseId}/note`,
        { note: opsNote, kind: 'ops' },
      );
      toast.success('Note added');
      setOpsNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Note failed');
    } finally {
      setNotesPosting(false);
    }
  };

  const submitReview = async () => {
    setReviewing(true);
    try {
      await axios.post(
        `${API}/formulation/production/${subscriberId}/${phaseId}/delivery-issue/review`,
        { review_note: reviewNote || undefined },
      );
      toast.success('Delivery issue marked reviewed');
      setReviewOpen(false);
      setReviewNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally {
      setReviewing(false);
    }
  };

  if (!user) return null;

  return (
    <AdminLayout>
      <div data-testid="formulation-production-page" className="px-6 py-6 max-w-6xl mx-auto">
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <Beaker className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Production Timeline</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">F2</Badge>
          </div>
          <p className="text-sm text-white/50">
            Internal cadence: payment → delivery → execution. Subscriber sees simplified labels only.
          </p>
          <p className="text-xs text-white/40 mt-1">
            <span className="font-mono">subscriber:</span> {subscriberId} · <span className="font-mono">phase:</span> {phaseId}
          </p>
        </div>

        {loading && (
          <div data-testid="production-loading" className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}
        {pageError && !loading && (
          <div data-testid="production-page-error" className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
            <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={load}>Retry</Button>
          </div>
        )}

        {data && (
          <>
            <StatusStrip
              data={data}
              canReview={canReview}
              onReviewClick={() => setReviewOpen(true)}
            />
            <ProgressBar status={data.production.status} />
            <ExpectedActualTable rec={data.production} />
            <RawBasesPanel rec={data.production} />

            {isAdmin && data.production.status !== 'cancelled' && (
              <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4 mb-4">
                <h3 className="text-sm font-medium text-white mb-3">Stage controls</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Move */}
                  <div className="space-y-2">
                    <Label className="text-[11px] text-white/60">Move to next status</Label>
                    <div className="flex gap-2">
                      <Select value={nextStatus} onValueChange={setNextStatus}>
                        <SelectTrigger data-testid="production-stage-next-select" className="h-9 text-xs bg-slate-950/40 border-white/10">
                          <SelectValue placeholder="Pick next status…" />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGE_ORDER.map((s) => (
                            <SelectItem key={s} value={s} disabled={s === data.production.status}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        data-testid="production-stage-next-confirm-btn"
                        onClick={moveStatus} disabled={!nextStatus || moving}
                        className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-9 text-xs"
                      >
                        {moving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm'}
                      </Button>
                    </div>
                  </div>
                  {/* Delay */}
                  <div className="space-y-2">
                    <Label className="text-[11px] text-white/60">Delay note</Label>
                    <Textarea
                      data-testid="production-delay-note-input"
                      rows={2}
                      value={delayNote}
                      onChange={(e) => setDelayNote(e.target.value)}
                      placeholder="Reason for delay (internal)…"
                      className="text-xs bg-slate-950/40 border-white/10"
                    />
                    <Button
                      data-testid="production-mark-delayed-btn"
                      variant="outline" onClick={markDelayed} disabled={delaying}
                      className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10 h-8 text-xs"
                    >
                      {delaying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3 mr-1" />}
                      Mark delayed
                    </Button>
                  </div>
                </div>

                {/* Mark Delivered */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Label className="text-[11px] text-white/60 block mb-2">Mark delivered (auto-schedules execution next day)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <Input
                      type="date"
                      data-testid="production-delivery-date-input"
                      value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}
                      className="h-9 text-xs bg-slate-950/40 border-white/10"
                    />
                    <Input
                      data-testid="production-courier-name-input"
                      placeholder="Courier name"
                      value={courierName} onChange={(e) => setCourierName(e.target.value)}
                      className="h-9 text-xs bg-slate-950/40 border-white/10"
                    />
                    <Input
                      data-testid="production-courier-tracking-input"
                      placeholder="Tracking #"
                      value={courierTracking} onChange={(e) => setCourierTracking(e.target.value)}
                      className="h-9 text-xs bg-slate-950/40 border-white/10"
                    />
                    <Button
                      data-testid="production-mark-delivered-btn"
                      onClick={markDelivered} disabled={delivering || data.production.status !== 'dispatched'}
                      className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 h-9 text-xs"
                    >
                      {delivering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5 mr-1" />}
                      Mark Delivered
                    </Button>
                  </div>
                </div>

                {/* Add note */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Label className="text-[11px] text-white/60 block mb-2">Add ops note (no status change)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={opsNote} onChange={(e) => setOpsNote(e.target.value)}
                      placeholder="Internal observation…"
                      className="flex-1 h-9 text-xs bg-slate-950/40 border-white/10"
                    />
                    <Button
                      onClick={addNote} disabled={!opsNote.trim() || notesPosting}
                      variant="outline" className="border-white/15 text-white/80 h-9 text-xs"
                    >
                      {notesPosting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add note'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <TimelinePanel events={data.timeline || []} />
          </>
        )}
      </div>

      {/* F3 — review delivery issue modal */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent
          data-testid="production-delivery-issue-review-modal"
          className="bg-slate-900 border border-white/10"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Mark delivery issue reviewed</DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              Add an optional note for the audit trail. This only records the review
              in the timeline — it does not send any notification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[11px] text-white/60">Review note (optional)</Label>
            <Textarea
              data-testid="production-delivery-issue-review-note-input"
              rows={3}
              maxLength={1000}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder="What action was taken or confirmed?"
              className="bg-slate-950/40 border-white/10 text-sm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewOpen(false)}
              className="border-white/15 text-white/80"
            >
              Cancel
            </Button>
            <Button
              data-testid="production-delivery-issue-review-confirm-btn"
              onClick={submitReview}
              disabled={reviewing}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30"
            >
              {reviewing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Mark reviewed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function StatusStrip({ data, canReview, onReviewClick }) {
  const p = data.production;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-3 flex flex-wrap gap-4 items-center text-xs">
      <div data-testid="production-status-pill" className="flex items-center gap-2">
        <span className="text-white/40">status:</span>
        <span className="px-2 py-0.5 rounded border bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">
          {p.status}
        </span>
      </div>
      <div><span className="text-white/40">delayed:</span> {p.is_delayed ? 'yes' : 'no'}</div>
      <div><span className="text-white/40">exec_start:</span> {p.execution_start_date || '—'}</div>
      <div><span className="text-white/40">delivered_on:</span> {p.actual_delivery_date || '—'}</div>
      {p.courier_tracking && (
        <div className="font-mono"><span className="text-white/40">tracking:</span> {p.courier_tracking}</div>
      )}
      {p.subscriber_confirmed_delivery && (
        <span
          data-testid="production-delivery-confirmed-chip"
          className="px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] flex items-center gap-1"
        >
          <CheckCircle2 className="w-3 h-3" /> Subscriber confirmed delivery
        </span>
      )}
      {p.delivery_issue_open && (
        <span
          data-testid="production-delivery-issue-badge"
          className="px-2 py-0.5 rounded border bg-amber-500/15 text-amber-200 border-amber-500/30 text-[10px] flex items-center gap-1"
        >
          <ShieldAlert className="w-3 h-3" /> Delivery issue reported
          {canReview && (
            <button
              type="button"
              data-testid="production-delivery-issue-review-btn"
              onClick={onReviewClick}
              className="ml-2 underline decoration-dotted hover:text-amber-100"
            >
              Mark reviewed
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function ProgressBar({ status }) {
  const idx = STAGE_ORDER.indexOf(status);
  const isCancelled = status === 'cancelled';
  return (
    <div data-testid="production-progress-bar" className="rounded-lg border border-white/10 bg-slate-900/40 p-3 mb-3 overflow-x-auto">
      <div className="flex items-center gap-1 min-w-[800px]">
        {STAGE_ORDER.map((s, i) => {
          const done = !isCancelled && i < idx;
          const cur = !isCancelled && i === idx;
          return (
            <React.Fragment key={s}>
              <div
                data-testid={`production-stage-${s}-step`}
                className={`px-2 py-1 rounded border text-[10px] flex items-center gap-1 whitespace-nowrap
                  ${cur ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40' : ''}
                  ${done ? 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30' : ''}
                  ${!done && !cur ? 'bg-slate-800/40 text-white/40 border-slate-700/40' : ''}
                `}
              >
                {done && <CheckCircle2 className="w-3 h-3" />}
                {STAGE_LABELS[s]}
              </div>
              {i < STAGE_ORDER.length - 1 && (
                <span className={`text-[10px] ${i < idx ? 'text-emerald-400' : 'text-white/20'}`}>─</span>
              )}
            </React.Fragment>
          );
        })}
        {isCancelled && (
          <span className="px-2 py-1 rounded border bg-rose-500/10 text-rose-200 border-rose-500/30 text-[10px] ml-3 flex items-center gap-1">
            <Ban className="w-3 h-3" /> cancelled
          </span>
        )}
      </div>
    </div>
  );
}

function ExpectedActualTable({ rec }) {
  const rows = [
    ['Raw received', rec.expected_raw_received_date, null],
    ['Fermentation done', rec.expected_fermentation_complete_date, null],
    ['Packing', rec.expected_packing_date, null],
    ['Dispatch', rec.expected_dispatch_date, rec.actual_dispatch_date],
    ['Delivery', rec.expected_delivery_date, rec.actual_delivery_date],
    ['Execution start', null, rec.execution_start_date],
  ];
  return (
    <div data-testid="production-expected-actual-table" className="rounded-lg border border-white/10 bg-slate-900/40 mb-3 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Stage</th>
            <th className="text-left px-3 py-2 font-medium">Expected</th>
            <th className="text-left px-3 py-2 font-medium">Actual</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, exp, act]) => (
            <tr key={label} className="border-b border-white/5">
              <td className="px-3 py-2 text-white/80">{label}</td>
              <td className="px-3 py-2 text-white/60">{exp || '—'}</td>
              <td className="px-3 py-2 text-white/60">{act || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RawBasesPanel({ rec }) {
  return (
    <div data-testid="production-raw-bases-table" className="rounded-lg border border-white/10 bg-slate-900/40 mb-3 overflow-hidden">
      <div className="px-3 py-2 border-b border-white/10 text-[11px] text-white/50">
        Raw bases in this batch (read-only — change phase items via Executable Protocol)
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Code</th>
            <th className="text-left px-3 py-2 font-medium">Planned qty</th>
          </tr>
        </thead>
        <tbody>
          {(rec.raw_bases || []).length === 0 && (
            <tr><td colSpan="2" className="px-3 py-3 text-white/40 text-center">No raw bases recorded</td></tr>
          )}
          {(rec.raw_bases || []).map((rb, i) => (
            <tr key={i} className="border-b border-white/5">
              <td className="px-3 py-2 font-mono text-cyan-200">{rb.raw_base_code}</td>
              <td className="px-3 py-2 text-white/70">{rb.planned_qty ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TimelinePanel({ events }) {
  return (
    <div data-testid="production-timeline-list" className="rounded-lg border border-white/10 bg-slate-900/40 mb-4 overflow-hidden">
      <div className="px-3 py-2 border-b border-white/10 text-[11px] text-white/50 flex items-center gap-2">
        <PackageCheck className="w-3.5 h-3.5" /> Timeline events ({events.length})
      </div>
      <div className="max-h-72 overflow-y-auto">
        {events.length === 0 && (
          <div className="px-3 py-4 text-white/40 text-xs text-center">No events yet</div>
        )}
        {events.map((ev) => (
          <div key={ev.id} data-testid={`production-timeline-event-${ev.id}`}
               className="px-3 py-2 border-b border-white/5 text-xs flex items-start gap-2">
            <span className="text-white/40 font-mono whitespace-nowrap">{(ev.at || '').slice(0, 19).replace('T', ' ')}</span>
            <span className="text-white/60 font-mono">{ev.actor}</span>
            <span className="text-white/80">
              {ev.from_status ? <><span className="text-white/40">{ev.from_status}</span> → </> : null}
              <span>{ev.to_status}</span>
            </span>
            {ev.note && <span className="text-white/50 italic">— {ev.note}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
